import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { OrganizationProfile } from '@hero/schema';
import { useOrgProfileApi } from '@hero/api-hooks';
import {
  BankIcon,
  BuildingIcon,
  FileTextIcon,
  HandCoinsIcon,
  OrderWaybillIcon,
} from '@hero/web-icons';
import { Spinner } from '../../../app/Spinner';
import { IconTabs, type IconTab, type IconTabIcon } from '../../../app/IconTabs';
import { CarrierBankingForm } from '../Section/CarrierBankingForm';
import { CarrierFinancingForm } from '../Section/CarrierFinancingForm';
import { CarrierLegalForm } from '../Section/CarrierLegalForm';
import { GeneralProfileForm } from '../Section/GeneralProfileForm';
import { WaybillSettingsManager } from './WaybillSettingsManager';
import { useCarrierProfileTabIds } from './useCarrierProfileTabs';

/**
 * Descriptor for one tab on the profile manager. Adding a new tab
 * (e.g. "Financing", "Notifications") is a single push into the
 * `TABS_FOR` map below — no manager changes required.
 *
 * `icon` is rendered by `IconTabs` next to the translated label —
 * keeps the trigger row visually scannable and consistent with
 * other tabbed surfaces that also lean on the shared component.
 */
interface OrgProfileTab {
  /** Stable id, used for the URL and the `Tabs.value` state. */
  id: string;
  /** i18n key under `m-organization:tabs.<id>`. */
  labelKey: 'general' | 'legal' | 'banking' | 'financing' | 'waybill';
  /** Icon rendered before the label in the tab trigger. */
  icon: IconTabIcon;
  /** Render the tab body. Receives the loaded profile + a saved-cb. */
  render: (args: {
    profile: Partial<OrganizationProfile> | undefined;
    onSaved: () => void;
    disabled?: boolean;
  }) => ReactNode;
}

/**
 * Per-variant tab layout. Today carrier-dashboard mounts all three
 * tabs; customer-dashboard (which historically called the same
 * manager) only needs General + Legal because customer orgs
 * don't carry bank credentials. Adding a future
 * `'admin'` / `'broker'` variant is one entry here.
 */
const TABS_FOR: Record<'customer' | 'carrier', OrgProfileTab[]> = {
  carrier: [
    {
      id: 'general',
      labelKey: 'general',
      icon: BuildingIcon,
      render: ({ profile, onSaved, disabled }) => (
        <GeneralProfileForm profile={profile} disabled={disabled} onSaved={() => onSaved()} />
      ),
    },
    {
      id: 'legal',
      labelKey: 'legal',
      icon: FileTextIcon,
      render: ({ profile, onSaved, disabled }) => (
        <CarrierLegalForm profile={profile} disabled={disabled} onSaved={() => onSaved()} />
      ),
    },
    {
      id: 'banking',
      labelKey: 'banking',
      icon: BankIcon,
      render: ({ profile, onSaved, disabled }) => (
        <CarrierBankingForm
          profile={profile}
          disabled={disabled}
          onSaved={() => onSaved()}
        />
      ),
    },
    {
      id: 'financing',
      labelKey: 'financing',
      icon: HandCoinsIcon,
      render: ({ profile, onSaved, disabled }) => (
        <CarrierFinancingForm
          profile={profile}
          disabled={disabled}
          onSaved={() => onSaved()}
        />
      ),
    },
    {
      id: 'waybill',
      labelKey: 'waybill',
      icon: OrderWaybillIcon,
      render: ({ profile, onSaved, disabled }) => (
        <WaybillSettingsManager
          profile={profile}
          disabled={disabled}
          onSaved={() => onSaved()}
        />
      ),
    },
  ],
  customer: [
    {
      id: 'general',
      labelKey: 'general',
      icon: BuildingIcon,
      render: ({ profile, onSaved, disabled }) => (
        <GeneralProfileForm profile={profile} disabled={disabled} onSaved={() => onSaved()} />
      ),
    },
    {
      id: 'legal',
      labelKey: 'legal',
      icon: FileTextIcon,
      render: ({ profile, onSaved, disabled }) => (
        <CarrierLegalForm profile={profile} disabled={disabled} onSaved={() => onSaved()} />
      ),
    },
  ],
};

export interface OrganizationProfileManagerProps {
  /**
   * Carrier-dashboard renders three tabs (General / Legal /
   * Banking); customer-dashboard renders two. Kept on the prop
   * surface for backwards compatibility with the legacy manager —
   * the actual tab list is data-driven via `TABS_FOR`.
   */
  variant: 'customer' | 'carrier';
  /** Called on any section save. Used by the dashboard pages to
   *  refetch siblings (e.g. the sidebar org card) or show extra
   *  toasts beyond the global mutation handler. */
  onSuccess?: () => void;
  /** Read-only mode — every field + Save button is disabled. */
  disabled?: boolean;
  /**
   * Controlled active tab id. When provided, the manager defers
   * to the parent for state and only emits `onTabChange` — useful
   * for the carrier-dashboard `?tab=banking` URL sync.
   */
  activeTab?: string;
  /** Fired when the user clicks a tab — paired with `activeTab`. */
  onTabChange?: (tabId: string) => void;
}

/**
 * `OrganizationProfileManager` — Tabbed orchestration layer for
 * the carrier / customer profile screens. Each tab mounts a
 * dedicated section form (`GeneralProfileForm`,
 * `CarrierLegalForm`, `CarrierBankingForm`) and owns its own RHF
 * instance + Save button — saves are independent so editing
 * Banking can never accidentally overwrite Legal (the design
 * goal of the section-keyed `OrgProfileService`).
 *
 * Loading is centralised here: the manager owns the
 * `useOrgProfileApi()` query and hands the snapshot to each tab.
 * That way switching tabs doesn't refetch, and a save from one
 * tab automatically rehydrates the other (the `orgProfile`
 * entity in `ENTITY_CONFIGS` invalidates the query on success).
 */
export function OrganizationProfileManager({
  variant,
  onSuccess,
  disabled,
  activeTab,
  onTabChange,
}: OrganizationProfileManagerProps) {
  const { t } = useTranslation(['m-organization']);
  const profileQuery = useOrgProfileApi();
  const carrierTabIds = useCarrierProfileTabIds();
  const tabs = useMemo(() => {
    const base = TABS_FOR[variant] ?? TABS_FOR.carrier;
    if (variant !== 'carrier') return base;
    return base.filter(tab => (carrierTabIds as readonly string[]).includes(tab.id));
  }, [variant, carrierTabIds]);

  // Internal state used when the parent leaves `activeTab` uncontrolled.
  const [internalTab, setInternalTab] = useState<string>(tabs[0]?.id ?? 'general');
  const requestedTab = activeTab ?? internalTab;
  const currentTab = tabs.some(tab => tab.id === requestedTab)
    ? requestedTab
    : (tabs[0]?.id ?? 'general');

  const onChange = (value: string) => {
    if (onTabChange) onTabChange(value);
    else setInternalTab(value);
  };

  const handleSaved = () => {
    toast.success(t('m-organization:toast.success.profile_updated'));
    onSuccess?.();
  };

  if (profileQuery.isLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <Spinner />
      </div>
    );
  }

  // Materialise the `IconTabs` config from the static descriptor +
  // the loaded profile. Building it inside render keeps the i18n
  // resolution tied to the current language and avoids the manager
  // re-implementing trigger / panel layout — `IconTabs` owns that
  // surface and renames stay one-line changes here.
  const profile = profileQuery.data;
  const iconTabs: IconTab[] = tabs.map(tab => ({
    id: tab.id,
    title: t(`m-organization:tabs.${tab.labelKey}`),
    icon: tab.icon,
    content: tab.render({ profile, onSaved: handleSaved, disabled }),
  }));

  return <IconTabs tabs={iconTabs} value={currentTab} onValueChange={onChange} />;
}
